import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { BaseEntity, SimpleTableConfig } from "@/components/shared/UniversalCRMTable/types/shared";
import {
  groupDataByField,
  groupDataByDualFields,
  detectGroupableFields,
  GroupedItem,
} from "@/lib/utils/crm/grouping-strategies";
import type { GroupingContext, GroupableField } from "./types";

export interface UseGroupingStateParams<T extends BaseEntity> {
  data: T[];
  config: SimpleTableConfig<T>;
}

export interface UseGroupingStateResult<T extends BaseEntity> {
  groupableFields: GroupableField[];
  processedData: (GroupedItem | T)[];
  groupingContext: GroupingContext;
  groupByField: string | null;
  dualGrouping: { primary: string | null; secondary: string | null };
  groupedData: GroupedItem[] | T[];
}

function collectPrimaryGroupKeys(groupedData: GroupedItem[]): Set<string> {
  const keys = new Set<string>();
  groupedData.forEach((item) => {
    if (item.type === "group-header" && item.groupKey) {
      keys.add(item.groupKey);
    }
  });
  return keys;
}

function collectNestedExpandedGroups(groupedData: GroupedItem[]): Map<string, Set<string>> {
  const nested = new Map<string, Set<string>>();
  groupedData.forEach((item) => {
    if (item.type === "group-header" && item.groupKeys && item.groupKeys.length === 2) {
      const [primaryKey, secondaryKey] = item.groupKeys as [string, string];
      if (!nested.has(primaryKey)) {
        nested.set(primaryKey, new Set());
      }
      nested.get(primaryKey)!.add(secondaryKey);
    }
  });
  return nested;
}

function filterGroupedItems(
  groupedData: GroupedItem[],
  expandedGroups: Set<string>,
  nestedExpandedGroups: Map<string, Set<string>>
): GroupedItem[] {
  return groupedData.filter((item) => {
    if (item.type === "group-header") {
      return true;
    }

    if (item.type === "data") {
      if (item.groupKeys && item.groupKeys.length === 2) {
        const [primaryKey, secondaryKey] = item.groupKeys as [string, string];
        const secondaryGroupsForPrimary = nestedExpandedGroups.get(primaryKey);
        const primaryExpanded =
          secondaryGroupsForPrimary !== undefined && secondaryGroupsForPrimary.size > 0;
        return primaryExpanded && secondaryGroupsForPrimary?.has(secondaryKey);
      }

      if (item.groupKey !== undefined && item.groupKey !== null) {
        return expandedGroups.has(item.groupKey);
      }
    }

    return true;
  });
}

/**
 * Manages grouping state, cached grouped data, and expand/collapse handlers.
 */
export function useGroupingState<T extends BaseEntity>({
  data,
  config,
}: UseGroupingStateParams<T>): UseGroupingStateResult<T> {
  const [groupByField, setGroupByField] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [dualGrouping, setDualGrouping] = useState<{
    primary: string | null;
    secondary: string | null;
  }>({ primary: null, secondary: null });
  const [nestedExpandedGroups, setNestedExpandedGroups] = useState<Map<string, Set<string>>>(
    new Map()
  );

  const groupableFields = useMemo(() => detectGroupableFields(config), [config]);

  const groupedData = useMemo((): GroupedItem[] | T[] => {
    if (!groupByField && !dualGrouping.primary) {
      return data;
    }

    if (dualGrouping.primary && dualGrouping.secondary) {
      return groupDataByDualFields(data, dualGrouping.primary, dualGrouping.secondary, config);
    }

    if (groupByField) {
      return groupDataByField(data, groupByField, config);
    }

    return data;
  }, [data, groupByField, dualGrouping.primary, dualGrouping.secondary, config]);

  const isSingleGrouping = Boolean(groupByField && !dualGrouping.primary);
  const isDualGrouping = Boolean(dualGrouping.primary && dualGrouping.secondary);

  // Identity of the current grouping config. Auto-expand runs ONCE per config so
  // an intentional "collapse all" (expandedGroups → empty) is not immediately
  // undone. Changing the grouping field yields a new key → default expand-all.
  const groupingKey = dualGrouping.primary
    ? `dual:${dualGrouping.primary}|${dualGrouping.secondary ?? ""}`
    : groupByField
      ? `single:${groupByField}`
      : null;
  const autoExpandedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isSingleGrouping) return;
    if (autoExpandedKeyRef.current === groupingKey) return;

    const allGroupKeys = collectPrimaryGroupKeys(groupedData as GroupedItem[]);
    if (allGroupKeys.size > 0) {
      setExpandedGroups(allGroupKeys);
      autoExpandedKeyRef.current = groupingKey;
    }
  }, [isSingleGrouping, groupingKey, groupedData]);

  useEffect(() => {
    if (!isDualGrouping) return;
    if (autoExpandedKeyRef.current === groupingKey) return;

    const newNestedExpanded = collectNestedExpandedGroups(groupedData as GroupedItem[]);
    if (newNestedExpanded.size > 0) {
      setNestedExpandedGroups(newNestedExpanded);
      autoExpandedKeyRef.current = groupingKey;
    }
  }, [isDualGrouping, groupingKey, groupedData]);

  const collapseAllGroups = useCallback(() => {
    if (isDualGrouping) {
      setNestedExpandedGroups(new Map());
    } else {
      setExpandedGroups(new Set());
    }
  }, [isDualGrouping]);

  const expandAllGroups = useCallback(() => {
    if (isDualGrouping) {
      setNestedExpandedGroups(collectNestedExpandedGroups(groupedData as GroupedItem[]));
    } else {
      setExpandedGroups(collectPrimaryGroupKeys(groupedData as GroupedItem[]));
    }
  }, [isDualGrouping, groupedData]);

  const processedData = useMemo((): (GroupedItem | T)[] => {
    if (!groupByField && !dualGrouping.primary) {
      return data;
    }

    return filterGroupedItems(
      groupedData as GroupedItem[],
      expandedGroups,
      nestedExpandedGroups
    );
  }, [groupedData, expandedGroups, nestedExpandedGroups, groupByField, dualGrouping.primary, data]);

  const handleToggleGroup = useCallback(
    (groupKey: string) => {
      if (dualGrouping.primary && dualGrouping.secondary) {
        setNestedExpandedGroups((prev) => {
          const newMap = new Map(prev);
          const groupsForPrimary = newMap.get(groupKey) || new Set();
          const newGroupsForPrimary = new Set(groupsForPrimary);

          if (newGroupsForPrimary.size === 0) {
            const secondaryGroups = new Set<string>();
            (groupedData as GroupedItem[]).forEach((item) => {
              if (
                item.type === "group-header" &&
                Array.isArray(item.groupKeys) &&
                item.groupKeys.length === 2
              ) {
                const [primaryKey, secondaryKey] = item.groupKeys as [string, string];
                if (primaryKey === groupKey) {
                  secondaryGroups.add(secondaryKey);
                }
              }
            });

            if (secondaryGroups.size > 0) {
              newMap.set(groupKey, secondaryGroups);
            }
          } else {
            newMap.delete(groupKey);
          }

          return newMap;
        });
      } else {
        setExpandedGroups((prev) => {
          const newSet = new Set(prev);
          if (newSet.has(groupKey)) {
            newSet.delete(groupKey);
          } else {
            newSet.add(groupKey);
          }
          return newSet;
        });
      }
    },
    [dualGrouping.primary, dualGrouping.secondary, groupedData]
  );

  const handleToggleNestedGroup = useCallback((parentKey: string, groupKey: string) => {
    setNestedExpandedGroups((prev) => {
      const newMap = new Map(prev);
      const parentGroups = newMap.get(parentKey) || new Set();
      const newParentGroups = new Set(parentGroups);

      if (newParentGroups.has(groupKey)) {
        newParentGroups.delete(groupKey);
      } else {
        newParentGroups.add(groupKey);
      }

      newMap.set(parentKey, newParentGroups);
      return newMap;
    });
  }, []);

  const handleGroupByFieldChange = useCallback((field: string | null) => {
    setGroupByField(field);
    setExpandedGroups(new Set());
    setNestedExpandedGroups(new Map());
  }, []);

  const handleDualGroupingChange = useCallback(
    (primary: string | null, secondary: string | null) => {
      setDualGrouping({ primary, secondary });
      setExpandedGroups(new Set());
      setNestedExpandedGroups(new Map());
    },
    []
  );

  const groupingContext: GroupingContext = useMemo(
    () => ({
      isGrouped: !!(groupByField || dualGrouping.primary),
      expandedGroups,
      nestedExpandedGroups,
      onToggleGroup: handleToggleGroup,
      onToggleNestedGroup: handleToggleNestedGroup,
      onCollapseAllGroups: collapseAllGroups,
      onExpandAllGroups: expandAllGroups,
      groupByField,
      setGroupByField: handleGroupByFieldChange,
      dualGrouping,
      setDualGrouping: handleDualGroupingChange,
    }),
    [
      groupByField,
      dualGrouping,
      expandedGroups,
      nestedExpandedGroups,
      handleToggleGroup,
      handleToggleNestedGroup,
      collapseAllGroups,
      expandAllGroups,
      handleGroupByFieldChange,
      handleDualGroupingChange,
    ]
  );

  return {
    groupableFields,
    processedData,
    groupingContext,
    groupByField,
    dualGrouping,
    groupedData,
  };
}
